import type { AdminCountryDto } from "@/lib/api/types";
import { memberRegionValue } from "@/lib/country-options";

/** Legacy admin scopes data by cookie <c>Region</c> (e.g. MALAYSIA). */
export const ADMIN_REGION_COOKIE = "Region";
export const ADMIN_REGION_DEFAULT = "MALAYSIA";
export const ADMIN_REGION_CHANGED_EVENT = "admin-region-changed";

export const ADMIN_REGION_ALLOWED = ["MALAYSIA", "SINGAPORE", "TAIWAN", "INDONESIA"] as const;

/** @deprecated Use {@link ADMIN_REGION_ALLOWED}. */
export const ADMIN_REGION_FALLBACKS = ADMIN_REGION_ALLOWED;

export type AdminRegionOption = { value: string; label: string };

export function readAdminRegionCookie(): string {
  if (typeof document === "undefined") return ADMIN_REGION_DEFAULT;
  const m = document.cookie.match(/(?:^|;\s*)Region=([^;]*)/);
  return m ? decodeURIComponent(m[1]).trim() || ADMIN_REGION_DEFAULT : ADMIN_REGION_DEFAULT;
}

export function writeAdminRegionCookie(region: string): string {
  const next = region.trim() || ADMIN_REGION_DEFAULT;
  if (typeof document !== "undefined") {
    document.cookie = `${ADMIN_REGION_COOKIE}=${encodeURIComponent(next)}; path=/; max-age=${60 * 60 * 24 * 365}`;
    window.dispatchEvent(new CustomEvent(ADMIN_REGION_CHANGED_EVENT, { detail: next }));
  }
  return next;
}

export function adminRegionHeaders(): HeadersInit {
  return { "X-Admin-Region": readAdminRegionCookie() };
}

/** Logistic cannot change country — always overwrite the cookie from the account. */
export function applySessionRegion(opts: {
  region?: string | null;
  lockRegion?: boolean;
}): void {
  if (!opts.lockRegion) return;
  const next = (opts.region ?? "").trim();
  if (!next) return;
  writeAdminRegionCookie(next);
}

export function canonicalAdminRegion(value?: string | null): (typeof ADMIN_REGION_ALLOWED)[number] | null {
  const n = (value ?? "").trim().toUpperCase();
  if (!n) return null;
  if (n === "MY" || n === "MYS") return "MALAYSIA";
  if (n === "SG" || n === "SGP") return "SINGAPORE";
  if (n === "TW" || n === "TWN") return "TAIWAN";
  if (n === "ID" || n === "IDN") return "INDONESIA";
  for (const allowed of ADMIN_REGION_ALLOWED) {
    if (n === allowed) return allowed;
  }
  return null;
}

export function buildAdminRegionOptions(
  countries: AdminCountryDto[],
  current?: string | null
): AdminRegionOption[] {
  const byKey = new Map<string, AdminRegionOption>();
  for (const c of countries) {
    const key =
      canonicalAdminRegion(memberRegionValue(c)) ??
      canonicalAdminRegion(c.region) ??
      canonicalAdminRegion(c.name);
    if (!key || byKey.has(key)) continue;
    byKey.set(key, { value: key, label: key });
  }
  for (const name of ADMIN_REGION_ALLOWED) {
    if (!byKey.has(name)) byKey.set(name, { value: name, label: name });
  }
  const merged = ADMIN_REGION_ALLOWED.map((name) => byKey.get(name)!);
  const region = (current ?? "").trim();
  if (region && !canonicalAdminRegion(region) && !merged.some((o) => o.value.toUpperCase() === region.toUpperCase())) {
    merged.unshift({ value: region, label: `${region} (saved)` });
  }
  return merged;
}
